import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCloud,
  faDownload,
  faRefresh,
} from '@fortawesome/free-solid-svg-icons';
import { Button } from 'react-daisyui';
import { useEffect, useState } from 'react';
import JSZip from 'jszip';
import { ToastContainer } from 'react-toastify';
import { Folder, Client, RootFolder, ExternalFileDownloadItem } from '@types';
import { useRollbar } from '@rollbar/react';
import PageContainer from '@/components/PageContainer';
import { DashboardWrapper } from '@/components/DashboardWrapper';
import { post, get as getApi } from '@/utils/api';
import { DashboardCard } from '@/components/DashboardCard';
import { FileBrowser } from '@/components/FileBrowser';
import ErrorAlert from '@/components/ErrorAlert/ErrorAlert';
import { Spinner } from '@/components/Spinner';
import { IUserContext, useUserContext } from '@/context/UserContext';
import { get } from '@/utils/queries';
import { alertErrorMessage, alertMessage } from '@/utils/alerts';
import useFileData from '@/hooks/useFileData';
import RefreshFileListModal from '@/pages/Files/RefreshFileListModal';

interface Props {
  removeImpersonatingAccountIdForInvestors?: boolean;
}

// eslint-disable-next-line import/prefer-default-export
export function Files({ removeImpersonatingAccountIdForInvestors }: Props) {
  const rollbar = useRollbar();
  const {
    auth,
    impersonatingAccountId,
    companyName,
    setimpersonatingAccountId,
  }: IUserContext = useUserContext(); // Use the context
  const { getFileData } = useFileData();
  const {
    data: rootFolder,
    isValidating: isLoading,
    error,
    mutate,
  } = impersonatingAccountId
    ? getFileData(impersonatingAccountId)
    : getFileData();

  const { data: companyData } = auth?.isClient
    ? get<Client[]>(`account/${auth.clients[0].id}`)
    : get<Client[]>('account/list');

  const [updatingInProgress, setUpdatingInProgress] = useState<boolean>(false);
  const [downloadingInProgress, setDownloadingInProgress] =
    useState<boolean>(false);
  const [downloadProgress, setDownloadProgress] = useState<{
    current: number;
    total: number;
    phase: 'downloading' | 'zipping';
  } | null>(null);
  const [showUpdateTeamsModal, setShowUpdateTeamsModal] =
    useState<boolean>(false);

  useEffect(() => {
    if (
      removeImpersonatingAccountIdForInvestors &&
      setimpersonatingAccountId &&
      auth?.isInvestor
    ) {
      setimpersonatingAccountId(null);
    }
  }, [
    removeImpersonatingAccountIdForInvestors,
    setimpersonatingAccountId,
    auth?.isInvestor,
  ]);

  const refreshFileList = async (fullSync?: boolean) => {
    setUpdatingInProgress(true);
    alertMessage(
      'bottom-center',
      'Syncing in progress. Please wait a few minutes and then refresh the page.'
    );
    try {
      if (impersonatingAccountId) {
        await post<RootFolder>(`files/updateTeams/${impersonatingAccountId}`, {
          forceFullSync: fullSync,
        });
      } else {
        await post<RootFolder>(
          'files/updateTeams',
          {
            async:
              auth?.isSuperAdmin ||
              (!auth?.isSuperAdmin && !auth?.clients?.length)
                ? true
                : '',
          },
          {
            timeout:
              auth?.isSuperAdmin ||
              (!auth?.isSuperAdmin && !auth?.clients?.length)
                ? 20000000
                : 60000,
          }
        );
      }
      await mutate();
    } catch (errorAPI) {
      if ((errorAPI as Error).message === 'request timed out') {
        alertErrorMessage(
          'The sync did not finish in time, please refresh your page and try again.'
        );
      }
    } finally {
      setUpdatingInProgress(false);
    }
  };

  const downloadAllFiles = async () => {
    if (!impersonatingAccountId && !auth?.isClient) return;
    const client = impersonatingAccountId
      ? companyData?.find(
          (c) => c.id === (impersonatingAccountId || auth?.clients[0].id)
        )
      : (companyData as Client | undefined);
    if (!client) return;
    const teamId = client.ms_team_id;
    if (!teamId) {
      rollbar.error(
        'No team ID found for client when attempting to download files',
        { clientId: client.id }
      );
      alertErrorMessage(
        'An error occurred generating the download. Try again or contact support.'
      );
      return;
    }

    setDownloadingInProgress(true);
    try {
      const result = await getApi<{
        files: ExternalFileDownloadItem[];
      }>(`file/download-urls/${teamId}`);
      const total = result.files.length;
      let completed = 0;
      setDownloadProgress({ current: 0, total, phase: 'downloading' });

      const zip = new JSZip();
      await Promise.all(
        result.files.map(async (file) => {
          const response = await fetch(file.downloadUrl);
          const blob = await response.blob();
          // The file paths from the API include the root folder ('External'), which we want to remove for the zip structure
          const zipPath = file.path.split('/').slice(1).join('/');
          zip.file(zipPath, blob);
          completed += 1;
          setDownloadProgress({
            current: completed,
            total,
            phase: 'downloading',
          });
        })
      );

      setDownloadProgress({ current: 0, total: 100, phase: 'zipping' });
      const content = await zip.generateAsync({ type: 'blob' }, ({ percent }) =>
        setDownloadProgress({
          current: Math.round(percent),
          total: 100,
          phase: 'zipping',
        })
      );

      const url = URL.createObjectURL(content);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${client.client_name}.zip`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      alertErrorMessage('Error downloading files. Please try again.');
    } finally {
      setDownloadingInProgress(false);
      setDownloadProgress(null);
    }
  };

  const getDownloadStatusMessage = () => {
    if (!downloadProgress) return 'Retrieving file list...';
    if (downloadProgress.phase === 'downloading')
      return `Downloading files... (${downloadProgress.current}/${downloadProgress.total})`;
    return `Creating ZIP... (${downloadProgress.current}%)`;
  };

  const triggerRefreshFileList = async () => {
    if (impersonatingAccountId) {
      setShowUpdateTeamsModal(true);
    } else {
      await refreshFileList();
    }
  };

  if (error) {
    return (
      <PageContainer name='client-list'>
        <DashboardWrapper>
          <ErrorAlert
            isSuperAdmin={auth?.isSuperAdmin}
            error={error as unknown}
            context='Files'
          />
        </DashboardWrapper>
      </PageContainer>
    );
  }

  const props = {
    ...(companyName && { companyName }),
    ...(companyData && { companyData }),
  };

  return (
    <PageContainer name='files'>
      <DashboardWrapper {...props}>
        <ToastContainer />
        {downloadingInProgress ? (
          <div className='fixed inset-0 bg-black opacity-70 z-[100] flex flex-col gap-3 items-center justify-center'>
            <Spinner className='w-12 h-12' />
            <p className='text-white text-sm font-medium'>
              {getDownloadStatusMessage()}
            </p>
            {downloadProgress ? (
              <progress
                className='progress progress-primary w-64'
                value={downloadProgress.current}
                max={downloadProgress.total}
              />
            ) : null}
          </div>
        ) : null}
        {showUpdateTeamsModal ? (
          <RefreshFileListModal
            isOpen={showUpdateTeamsModal}
            onClose={() => setShowUpdateTeamsModal(false)}
            refreshFileList={refreshFileList}
          />
        ) : null}
        <div className='p-4'>
          <div className='flex w-full justify-between items-center mb-4'>
            <h1 className='text-lg font-bold uppercase'>
              <FontAwesomeIcon
                icon={faCloud}
                size='lg'
                className='mr-4 text-white'
              />
              {!auth?.isAdmin || impersonatingAccountId
                ? 'Shared Files'
                : 'All External Files'}
            </h1>
            <div>
              {auth?.isAdmin ? (
                <Button
                  color='accent'
                  id='refresh'
                  size='sm'
                  // eslint-disable-next-line @typescript-eslint/no-misused-promises
                  onClick={triggerRefreshFileList}
                >
                  <FontAwesomeIcon
                    icon={faRefresh}
                    size='lg'
                    className={`mr-2 text-white ${
                      updatingInProgress ? 'spinning' : ''
                    }`}
                  />
                  {impersonatingAccountId ? 'Refresh client' : 'Refresh'}
                </Button>
              ) : null}
              {auth?.isClient ||
              (impersonatingAccountId && !auth?.isInvestor) ? (
                <Button
                  color='accent'
                  size='sm'
                  disabled={downloadingInProgress}
                  // eslint-disable-next-line @typescript-eslint/no-misused-promises
                  onClick={downloadAllFiles}
                  className='ml-2'
                >
                  <FontAwesomeIcon
                    icon={faDownload}
                    size='lg'
                    className='mr-2 text-white'
                  />
                  {downloadingInProgress ? 'Downloading...' : 'Download All'}
                </Button>
              ) : null}
            </div>
          </div>
          <DashboardCard className='p-4' cardBodyclassName='p-0'>
            <FileBrowser
              rootFolder={rootFolder as Folder}
              isLoading={isLoading}
            />
          </DashboardCard>
        </div>
      </DashboardWrapper>
    </PageContainer>
  );
}
